import React, { useState } from 'react'
import { Link } from '@inertiajs/react'
import UserLayout from '../../../Layouts/UserLayout'

const ROWS_COUNT = 50

function parseKeywords(input) {
  return input
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
}

export default function BulkGenerate() {
  const [queries, setQueries] = useState(Array(ROWS_COUNT).fill(''))
  const [secondaryKeywordsInput, setSecondaryKeywordsInput] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [results, setResults] = useState(null)

  const handleQueryChange = (index, value) => {
    const next = [...queries]
    next[index] = value
    setQueries(next)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const validQueries = queries.map(q => q.trim()).filter(Boolean)
    if (validQueries.length === 0) {
      alert('Please enter at least one query.')
      return
    }

    setIsGenerating(true)
    setResults(null)

    try {
      const response = await fetch('/admin/blog_posts/bulk_create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          queries: validQueries,
          secondary_keywords: parseKeywords(secondaryKeywordsInput)
        })
      })

      const data = await response.json()

      if (data.success) {
        setResults(data)
        if (data.created_count > 0) {
          const createdQueries = new Set(data.created_posts.map(p => p.query))
          setQueries(prev => prev.map(q => createdQueries.has(q.trim()) ? '' : q))
        }
      } else {
        alert('Bulk generation failed. Check console.')
      }
    } catch (error) {
      console.error('Bulk generation error:', error)
      alert('Failed to generate posts. Check console.')
    } finally {
      setIsGenerating(false)
    }
  }

  const filledRowsCount = queries.filter(q => q.trim().length > 0).length

  return (
    <UserLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Bulk Blog Post Generator</h1>
          <Link href="/admin/blog_posts" className="text-gray-600 hover:text-gray-900">
            ← Back to Blog Posts
          </Link>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Secondary keywords (applies to all)</label>
            <input
              type="text"
              value={secondaryKeywordsInput}
              onChange={e => setSecondaryKeywordsInput(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm border p-2"
              disabled={isGenerating}
              placeholder="e.g. vocabulary, immersion, reading practice"
            />
          </div>

          <div className="text-sm text-gray-600">
            <p>• Generates a full SEO blog post + banner image per query</p>
            <p>• Posts are created as drafts (publish from the edit screen)</p>
            <p>• Filled rows: {filledRowsCount} / {ROWS_COUNT}</p>
          </div>
        </div>

        {results && (
          <div className={`mb-6 p-4 rounded-lg ${
            results.errors.length > 0
              ? 'bg-yellow-50 border border-yellow-200'
              : 'bg-green-50 border border-green-200'
          }`}>
            <h3 className="font-semibold mb-2">
              Generation Results: {results.created_count} created
            </h3>
            {results.created_posts.length > 0 && (
              <div className="mb-2">
                <p className="text-sm font-medium mb-1">Successfully created:</p>
                <ul className="text-sm list-disc list-inside space-y-1">
                  {results.created_posts.slice(0, 10).map((item, i) => (
                    <li key={i}>
                      <Link
                        href={`/admin/blog_posts/${item.slug}/edit`}
                        className="text-emerald-600 hover:underline"
                        target="_blank"
                      >
                        {item.query} → {item.title}
                      </Link>
                      <span className="text-gray-500 ml-2 font-mono">/blog/{item.slug}</span>
                    </li>
                  ))}
                  {results.created_posts.length > 10 && (
                    <li className="text-gray-500">... and {results.created_posts.length - 10} more</li>
                  )}
                </ul>
              </div>
            )}
            {results.errors.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-1 text-red-700">Errors:</p>
                <ul className="text-sm text-red-600 list-disc list-inside space-y-1">
                  {results.errors.map((error, i) => (
                    <li key={i}>
                      {error.query}: {error.errors.join(', ')}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <h2 className="text-lg font-semibold">Primary queries</h2>
            <button
              type="submit"
              disabled={isGenerating || filledRowsCount === 0}
              className="bg-indigo-600 text-white px-6 py-2 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? `Generating ${filledRowsCount}…` : `Generate ${filledRowsCount} drafts`}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="p-3 text-left text-sm font-medium text-gray-700 w-12">#</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Query</th>
                </tr>
              </thead>
              <tbody>
                {queries.map((query, index) => (
                  <tr key={index} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-500">{index + 1}</td>
                    <td className="p-3">
                      <input
                        type="text"
                        value={query}
                        onChange={e => handleQueryChange(index, e.target.value)}
                        placeholder={`Enter query ${index + 1}...`}
                        disabled={isGenerating}
                        className="w-full rounded-md border-gray-300 shadow-sm border p-2 text-sm focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </form>
      </div>
    </UserLayout>
  )
}


